import type { CSSProperties, HTMLAttributes, ReactElement } from 'react';
import type { ThinkingAuraSettings } from './thinkingAura';

interface ThinkingAuraTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  children: string;
  settings: ThinkingAuraSettings;
  reducedMotion: boolean;
}

/**
 * Two independent text layers keep the resting opacity and moving highlight
 * from affecting one another. The highlight's first and last frames are both
 * fully transparent, so the CSS animation can restart without a visible snap.
 */
export function ThinkingAuraText({
  children,
  settings,
  reducedMotion,
  className = '',
  style,
  ...rest
}: ThinkingAuraTextProps): ReactElement {
  const shimmerEnabled = !reducedMotion && (settings.textEffect === 'shimmer' || settings.textEffect === 'both');
  const glowEnabled = settings.textEffect === 'glow' || settings.textEffect === 'both';
  const band = settings.textBandWidth;
  const core = Math.max(0.02, band * (1 - settings.textFeather));
  const gradient = settings.textGradientMode === 'custom'
    ? [settings.textGradient1, settings.textGradient2, settings.textGradient3]
    : ['var(--dock-aura-text)', 'var(--dock-aura-text)', 'var(--dock-aura-text)'];
  const classes = [className, 'thinking-aura-text', glowEnabled ? 'thinking-aura-text-glow' : '']
    .filter(Boolean)
    .join(' ');
  const textStyle = {
    '--thinking-text-base-color': `color-mix(in srgb, var(--dock-aura-text) ${Math.round(settings.textBaseOpacity * 100)}%, transparent)`,
    '--thinking-text-highlight-opacity': settings.textHighlightOpacity,
    '--thinking-text-duration': `${settings.textSpeed}s`,
    '--thinking-text-band-start': `${50 - band}%`,
    '--thinking-text-core-start': `${50 - core}%`,
    '--thinking-text-core-end': `${50 + core}%`,
    '--thinking-text-band-end': `${50 + band}%`,
    '--thinking-text-gradient-1': gradient[0],
    '--thinking-text-gradient-2': gradient[1],
    '--thinking-text-gradient-3': gradient[2],
    '--thinking-text-tracking': `${settings.textTracking}px`,
    '--thinking-text-glow-blur': `${4 + settings.textGlow * 16}px`,
    '--thinking-text-glow-color': `color-mix(in srgb, var(--dock-aura-glow) ${Math.round(settings.textGlow * 100)}%, transparent)`,
    ...style,
  } as CSSProperties;

  return (
    <span className={classes} style={textStyle} {...rest}>
      <span className="thinking-aura-text-base">{children}</span>
      {shimmerEnabled ? <span className="thinking-aura-text-highlight" aria-hidden="true">{children}</span> : null}
    </span>
  );
}
